/**
 * Reading a run-log entry back as people read the board: the columns a rule
 * wrote, by their labels, and the values as the cells would show them.
 */
import type { TrackerAutomationRun } from '@/entities/tracker-automation';
import { i18n } from '@/shared/i18n';

import { GROUP_VALUE_KEY } from './grouping';
import { parseLocalDate } from './parseLocalDate';
import type { CustomColumn, StatusOption } from './useTrackersBoard';

export interface RunChange {
  label: string;
  value: string;
}

/** Several phrases joined the way the reader's language joins a list. */
export function listText(items: string[]): string {
  return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(items);
}

/** A moment in full, for the hover on a relative time. */
export function dateTimeText(iso: string): string {
  const date = parseLocalDate(iso);
  return date
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
        date,
      )
    : iso;
}

// parseLocalDate reads a stored day as local midnight, so it must be shown on the local
// clock too: a UTC formatter would print the previous day east of UTC.
const dayFormat = () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

function dayText(value: string): string {
  const date = parseLocalDate(value);
  return date ? dayFormat().format(date) : value;
}

function rangeText(start: unknown, end: unknown): string {
  const from = typeof start === 'string' ? start : null;
  const to = typeof end === 'string' ? end : null;
  const fromDate = parseLocalDate(from);
  const toDate = parseLocalDate(to);
  if (fromDate && toDate) return dayFormat().formatRange(fromDate, toDate);
  const parts = [from, to].filter((d): d is string => d !== null);
  return parts.length === 1 ? dayText(parts[0]) : parts.join(' – ');
}

function cellText(value: unknown, column: CustomColumn | undefined): string {
  if (value === null || value === undefined || value === '')
    return i18n.t('trackers:trackersBoard.automationRuns.cleared');
  if (Array.isArray(value)) {
    if (value.length === 0) return i18n.t('trackers:trackersBoard.automationRuns.cleared');
    return column?.type === 'people'
      ? i18n.t('trackers:trackersBoard.automationRuns.people', { count: value.length })
      : listText(value.map((v) => cellText(v, undefined)));
  }
  if (typeof value === 'boolean')
    return value
      ? i18n.t('trackers:trackersBoard.automationRuns.checked')
      : i18n.t('trackers:trackersBoard.automationRuns.unchecked');
  if (typeof value === 'object') {
    const { start, end } = value as { start?: unknown; end?: unknown };
    if (typeof start === 'string' || typeof end === 'string') return rangeText(start, end);
    return i18n.t('trackers:trackersBoard.automationRuns.changed');
  }
  const option = column?.statusOptions?.find((o) => o.id === value);
  if (option) return option.label;
  if (column?.type === 'date' && typeof value === 'string') return dayText(value);
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : i18n.t('trackers:trackersBoard.automationRuns.changed');
}

/** The name a written key goes by: its column's label, or "Group" for a group move. */
export function writtenKeyLabel(key: string, columns: CustomColumn[]): string {
  if (key === GROUP_VALUE_KEY) return i18n.t('trackers:trackersBoard.automationRuns.group');
  return (
    columns.find((c) => c.id === key)?.label ??
    i18n.t('trackers:trackersBoard.automationRuns.aDeletedColumn')
  );
}

/** A written value as the board shows it under `key`. */
export function writtenValueText(
  key: string,
  value: unknown,
  columns: CustomColumn[],
  groups: StatusOption[],
): string {
  if (key === GROUP_VALUE_KEY) {
    return (
      groups.find((g) => g.id === value)?.label ??
      i18n.t('trackers:trackersBoard.automationRuns.aDeletedGroup')
    );
  }
  return cellText(
    value,
    columns.find((c) => c.id === key),
  );
}

export function describeRunChanges(
  changes: Record<string, unknown> | null | undefined,
  columns: CustomColumn[],
  groups: StatusOption[],
): RunChange[] {
  if (!changes) return [];
  return Object.entries(changes).map(([key, value]) => ({
    label: writtenKeyLabel(key, columns),
    value: writtenValueText(key, value, columns, groups),
  }));
}

type RunEffect = NonNullable<TrackerAutomationRun['effects']>[number];

/**
 * What a run's notify and comment actions did, one phrase each. A try that
 * found an action already done by an earlier one reads as done: the firing
 * ran it, once.
 */
export function describeRunEffects(effects: RunEffect[] | null | undefined): string[] {
  return (effects ?? []).flatMap((effect) => {
    const left = effect.droppedCount
      ? [
          i18n.t('trackers:trackersBoard.automationRuns.effects.peopleLeftOut', {
            count: effect.droppedCount,
          }),
        ]
      : [];
    return [...describeEffect(effect), ...left];
  });
}

function describeEffect(effect: RunEffect): string[] {
  const ran = effect.status === 'done' || effect.status === 'already_done';
  // Only an AI action queues work of its own; any other action logged as
  // queued is one a firing held back until its AI actions are over.
  if (effect.status === 'queued' && effect.type !== 'ai_fill' && effect.type !== 'run_agent') {
    return [i18n.t('trackers:trackersBoard.automationRuns.effects.heldForAi')];
  }
  switch (effect.type) {
    case 'notify':
      if (ran) {
        return [
          i18n.t('trackers:trackersBoard.automationRuns.effects.notifyDone', {
            count: effect.recipientCount ?? 0,
          }),
        ];
      }
      return [
        effect.status === 'skipped'
          ? i18n.t('trackers:trackersBoard.automationRuns.effects.notifySkipped')
          : i18n.t('trackers:trackersBoard.automationRuns.effects.notifyFailed'),
      ];
    case 'comment':
      if (ran) return [i18n.t('trackers:trackersBoard.automationRuns.effects.commentDone')];
      return [
        effect.status === 'skipped'
          ? i18n.t('trackers:trackersBoard.automationRuns.effects.commentSkipped')
          : i18n.t('trackers:trackersBoard.automationRuns.effects.commentFailed'),
      ];
    case 'webhook':
    case 'slack': {
      const keys = {
        webhook: {
          ran: 'trackers:trackersBoard.automationRuns.effects.webhookDone',
          skipped: 'trackers:trackersBoard.automationRuns.effects.webhookSkipped',
          failed: 'trackers:trackersBoard.automationRuns.effects.webhookFailed',
        },
        slack: {
          ran: 'trackers:trackersBoard.automationRuns.effects.slackDone',
          skipped: 'trackers:trackersBoard.automationRuns.effects.slackSkipped',
          failed: 'trackers:trackersBoard.automationRuns.effects.slackFailed',
        },
      } as const;
      const key = keys[effect.type];
      return [i18n.t(ran ? key.ran : effect.status === 'skipped' ? key.skipped : key.failed)];
    }
    case 'create_row':
    case 'duplicate_row':
    case 'move_row':
    case 'archive_row': {
      const keys = {
        create_row: 'trackers:trackersBoard.automationRuns.effects.rowCreated',
        duplicate_row: 'trackers:trackersBoard.automationRuns.effects.rowCopied',
        move_row: 'trackers:trackersBoard.automationRuns.effects.rowMoved',
        archive_row: 'trackers:trackersBoard.automationRuns.effects.rowArchived',
      } as const;
      if (ran) return [i18n.t(keys[effect.type])];
      if (effect.status === 'skipped_no_access_target') {
        return [i18n.t('trackers:trackersBoard.automationRuns.effects.noAccessToTargetBoard')];
      }
      if (effect.status === 'skipped_no_access') {
        return [i18n.t('trackers:trackersBoard.automationRuns.effects.noAccessToSourceBoard')];
      }
      return [
        effect.status === 'skipped'
          ? i18n.t('trackers:trackersBoard.automationRuns.effects.rowActionSkipped')
          : i18n.t('trackers:trackersBoard.automationRuns.effects.rowActionFailed'),
      ];
    }
    case 'ai_fill':
      return [describeAiFill(effect)];
    case 'run_agent':
      return [describeAgentRun(effect)];
    default:
      return [];
  }
}

const AI_REASONS = [
  'budget_reached',
  'model_not_allowed',
  'model_access_unknown',
  'no_edit_access',
  'no_answer',
  'invalid_output',
  'ai_call_failed',
  'internal_error',
  'rule_changed',
  'row_gone',
  'agent_not_allowed',
  'agent_not_supported',
  'agent_not_found',
  'agent_run_pending',
  'agent_input_changed',
  'after_ai_write',
  'ai_workspace_missing',
  'organization_missing',
] as const;

function describeAiFill(effect: RunEffect): string {
  if (effect.status === 'queued') {
    return i18n.t('trackers:trackersBoard.automationAi.runs.fillQueued');
  }
  if (effect.status === 'done' || effect.status === 'already_done') {
    return i18n.t('trackers:trackersBoard.automationAi.runs.fillDone', {
      model: effect.model ?? '',
      promptVersion: effect.promptVersion ?? '',
    });
  }
  const reason = AI_REASONS.find((r) => r === effect.reason);
  if (reason) return i18n.t(`trackers:trackersBoard.automationAi.reasons.${reason}`);
  return effect.status === 'skipped'
    ? i18n.t('trackers:trackersBoard.automationAi.runs.skipped')
    : i18n.t('trackers:trackersBoard.automationAi.runs.fillFailed');
}

function describeAgentRun(effect: RunEffect): string {
  if (effect.status === 'queued') {
    return i18n.t('trackers:trackersBoard.automationAi.runs.agentQueued');
  }
  if (effect.status === 'done' || effect.status === 'already_done') {
    return i18n.t('trackers:trackersBoard.automationAi.runs.agentDone', {
      model: effect.model ?? '',
    });
  }
  const reason = AI_REASONS.find((r) => r === effect.reason);
  if (reason) return i18n.t(`trackers:trackersBoard.automationAi.reasons.${reason}`);
  return effect.status === 'skipped'
    ? i18n.t('trackers:trackersBoard.automationAi.runs.skipped')
    : i18n.t('trackers:trackersBoard.automationAi.runs.agentFailed');
}
